import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import Page from "utils/Page";
import { asTitle, isEmailValid } from "utils/Utils";
import { sendPasswordResetLink } from "./AuthenticationService";

const RequestPasswordResetPage: React.FC = () => {
	const { t } = useTranslation();

	const [email, setEmail] = useState("");
	const [emailValid, setEmailValid] = useState(true);
	const [loading, setLoading] = useState(false);
	const [linkSent, setLinkSent] = useState(false);
	const [error, setError] = useState(false);

	function onEmailChange(event: React.ChangeEvent<HTMLInputElement>): void {
		event.preventDefault();
		const emailValue = event.target.value;
		setEmail(emailValue);
		setEmailValid(isEmailValid(emailValue));
	}

	function isFormValid(): boolean {
		if (!isEmailValid(email)) {
			setEmailValid(false);
			return false;
		}

		return true;
	}

	function submit(
		event: React.MouseEvent<HTMLButtonElement, MouseEvent>
	): void {
		setError(false);
		setLinkSent(false);
		event.preventDefault();
		if (isFormValid()) {
			if (!loading) {
				setLoading(true);
				sendPasswordResetLink(email)
					.then(() => {
						setLinkSent(true);
						setEmail("");
					})
					.catch(() => setError(true))
					.finally(() => setLoading(false));
			}
		}
	}

	return (
		<Grid container direction="row" justifyContent="center">
			<Grid item xs={12} sm={8} md={6} lg={4}>
				<Paper sx={{ margin: 2, padding: 2, textAlign: "center" }}>
					<Typography variant="h5" gutterBottom>
						{asTitle(t("resetPasswordTitle"))}
					</Typography>
					{error && (
						<Alert id="error-message" severity="error">
							{t("passwordResetLinkError")}
						</Alert>
					)}
					{linkSent && (
						<Alert id="success-message" severity="success">
							{t("passwordResetLinkSent")}
						</Alert>
					)}
					<Box
						component="form"
						sx={{
							"& .MuiTextField-root, .MuiButton-root, .MuiFormControl-root":
								{
									marginTop: 1,
									marginBottom: 1
								}
						}}>
						{" "}
						<TextField
							required
							fullWidth
							id="email"
							type="email"
							label={t("email")}
							error={!emailValid}
							value={email}
							onChange={onEmailChange}
							variant="standard"
						/>
						<Button
							id="login"
							variant="contained"
							fullWidth
							disabled={loading}
							onClick={submit}>
							{t("sendLinkButton")}
						</Button>
						<Typography variant="body1">
							<Link to="/login">{t("toLogin")}</Link>
						</Typography>
					</Box>
				</Paper>
			</Grid>
		</Grid>
	);
};

export default RequestPasswordResetPage;
